import { useMemo } from "react";
import { differenceInDays } from "date-fns";
import { useSelectorApp } from "../lib/redux";
import { HOME_FEATURES } from "../data/homeFeatures";
import { HOME_FEATURE_RECENT_USAGE_DAYS } from "../utils/constants";

const isFeatureRecentlyUsed = ({ count, lastUsedAt }) => {
  if (count === 0) return false;
  if (!lastUsedAt) return true;

  return (
    differenceInDays(new Date(), new Date(lastUsedAt)) <
    HOME_FEATURE_RECENT_USAGE_DAYS
  );
};

const useHomeFeatures = () => {
  const featuresActivity = useSelectorApp(
    (state) => state.homeFeatureActivity.features
  );
  const isLoading = useSelectorApp(
    (state) => state.homeFeatureActivity.isLoading
  );

  const mergedFeatures = useMemo(
    () =>
      HOME_FEATURES.map((feature) => {
        const activity = featuresActivity?.[feature.id] || {};
        const count = activity.count || 0;
        const lastUsedAt = activity.lastUsedAt ?? null;

        return {
          ...feature,
          count,
          lastUsedAt,
          isRecentlyUsed: isFeatureRecentlyUsed({ count, lastUsedAt }),
        };
      }),
    [featuresActivity]
  );

  const activeFeatures = useMemo(
    () =>
      mergedFeatures
        .filter((feature) => feature.isRecentlyUsed)
        .toSorted((a, b) => b.count - a.count),
    [mergedFeatures]
  );

  const unusedFeatures = useMemo(
    () =>
      mergedFeatures
        .filter((feature) => !feature.isRecentlyUsed)
        .toSorted((a, b) => {
          if (a.count === 0 && b.count > 0) return -1;
          if (a.count > 0 && b.count === 0) return 1;
          if (a.count === 0 && b.count === 0) return 0;

          return (a.lastUsedAt ?? 0) - (b.lastUsedAt ?? 0);
        }),
    [mergedFeatures]
  );

  return {
    activeFeatures,
    unusedFeatures,
    isLoading,
  };
};

export default useHomeFeatures;
